import { requiredValidate, getHoverRow, getDataSourceByAlias, mergeDetailCopyData } from '../utils';
import { isJDiwork, parseMobxObj } from '@utils';
import { PageStatusType } from '@stores/pagestore';
import { utils } from 'ynf-core-middleware';
import ErrDisplay from '../../stores/cardstore/ErrDisplay'
import * as services from "@api";

/**
 * 打开单据-树表方式
 * @param rootStore
 * @param options {
 *  // 必填字段
 *  treeStoreKey: string， 默认值：treeStore
 *  billNo: string，跳转卡片页的billNo
 *  mode: 'edit|browse|add'
 *  foreignKey: string
 * }
 * @returns
 * @hooks {
 *  // 专有钩子
 *  // 公共钩子 
 *  beforeHook: 开始钩子 
 *  afterHook: 结束钩子
 *  catchHook: 异常钩子
 *  finallyHook: 最终钩子
 * }
 */
export const openBillByTree = {
  get isOperationLog() {
    const { type } = this.params;
    if (type === 'modal') {
      return true;
    } else if (type === 'tab' && isJDiwork()) {
      return false;
    } else {
      const { pageStore = {} } = this.rootStore;
      const { generalSetting = {} } = pageStore;
      return generalSetting.tabOpenMode !== 1;
    }
  },
  async doAction (rootStore: any, options: any = {}) {
    requiredValidate.call(this, options, ['treeStoreKey', 'foreignKey', 'billNo']);
    const { treeStoreKey = 'treeStore', ...params } = options;
    const treeStore = rootStore.get(treeStoreKey);
    if (!treeStore) throw (new Error(`The TreeStore[${treeStoreKey}] not found`));
    return openBill.doAction.call(this, rootStore, {
      ...params,
      treeKey: treeStore.selectedKeys?.[0]
    });
  }
}

/**
 * 打开Link
 * @param rootStore
 * @param options {
 *  // 必填字段
 *  url: string
 *  // 选填字段
 *  query：object，查询参数
 * }
 * @hooks {
 *  // 专有钩子
 *  // 公共钩子
 *  beforeHook: 开始钩子
 *  afterHook: 结束钩子
 *  catchHook: 异常钩子
 *  finallyHook: 最终钩子
 * }
 */
export const openLink = {
  async doAction (rootStore: any, options: any = {}) {
    requiredValidate.call(this, options, ['url']);
    let { url = '', query = {} } = options;
    window.open(utils.setUrlParams(url, parseMobxObj(rootStore, query)));
  }
}

/**
 * 打开单据
 * @param rootStore
 * @param options {
 *  // 必填字段
 *  billNo: string，跳转卡片页的billNo
 *  mode: 'edit|browse|add'
 *  // 选填字段
 *  type: 'route|modal|tab'，默认：route
 *  billId: string
 *  title: string 
 * }
 * @hooks {
 *  // 专有钩子
 *  // 公共钩子
 *  beforeHook: 开始钩子
 *  afterHook: 结束钩子
 *  catchHook: 异常钩子
 *  finallyHook: 最终钩子
 * }
 */
export const openBill = {
  get isOperationLog() {
    const { type } = this.params;
    if (type === 'modal') {
      return true;
    } else if (type === 'tab' && isJDiwork()) {
      return false;
    } else {
      const { pageStore = {} } = this.rootStore;
      const { generalSetting = {} } = pageStore;
      return generalSetting.tabOpenMode !== 1;
    }
  },
  async doAction (rootStore: any, options: any = {}) {
    const { type, ...params } = options;
    if (type === 'modal') {
      return openBillByModal.call(this, rootStore, params);
    } else if (type === 'tab' && isJDiwork()) {
      return openBillByTabs.doAction.call(this, rootStore, params);
    } else {
      return openBillByRoute.doAction.call(this, rootStore, params);
    }
  }
}

/**
 * 打开单据-路由方式
 * @param rootStore
 * @param options {
 *  // 必填字段
 *  billNo: string，跳转卡片页的billNo
 *  mode: 'edit|browse|add'
 *  // 选填字段
 *  billId: string 
 * }
 * @returns
 * @hooks {
 *  // 专有钩子
 *  // 公共钩子 
 *  beforeHook: 开始钩子 
 *  afterHook: 结束钩子
 *  catchHook: 异常钩子
 *  finallyHook: 最终钩子
 * }
 */
export const openBillByRoute = {
  get isOperationLog() {
    const { pageStore = {} } = this.rootStore;
    const { generalSetting = {} } = pageStore;
    return generalSetting.tabOpenMode !== 1;
  },
  async doAction (rootStore: any, options: any = {}) {
    requiredValidate.call(this, options, ['billNo', 'mode']);
    const { pageStore = {} } = rootStore;
    const { billNo, status, ...params } = options;
    try {
      return pageStore.push({
        billNo,
        params,
        status: status ?? params.mode
      });
    } catch (err: any) {
      return Promise.reject(err);
    }
  }
}

/**
 * 打开单据-模态框方式
 * @param rootStore
 * @param options {
 *  // 必填字段
 *  billNo: string，跳转卡片页的billNo
 *  mode: 'edit|browse|add'
 *  // 选填字段
 *  billId: string 
 *  modalProps: object 
 * }
 * @returns
 * @hooks {
 *  // 专有钩子
 *  // 公共钩子 
 *  beforeHook: 开始钩子 
 *  afterHook: 结束钩子
 *  catchHook: 异常钩子
 *  finallyHook: 最终钩子
 * }
 */
export async function openBillByModal(rootStore: any, options: any = {}) {
  requiredValidate.call(this, options, ['billNo', 'mode']);
  const { pageStore = {} } = rootStore;
  const { billNo, status, modalProps = {}, query = {}, ...params } = options;
  try {
    return pageStore.openModal({
      billNo,
      query,
      params,
      status: status ?? params.mode,
      ...modalProps
    });
  } catch (err: any) {
    return Promise.reject(err);
  }
}

/**
 * 打开单据-新页签方式
 * @param rootStore
 * @param options {
 *  // 必填字段
 *  billNo: string，跳转卡片页的billNo
 *  mode: 'edit|browse|add'
 *  // 选填字段
 *  billId: string 
 *  modalProps: object 
 * }
 * @returns
 * @hooks {
 *  // 专有钩子
 *  // 公共钩子 
 *  beforeHook: 开始钩子 
 *  afterHook: 结束钩子
 *  catchHook: 异常钩子
 *  finallyHook: 最终钩子
 * }
 */
export const openBillByTabs = {
  isOperationLog: false,
  async doAction (rootStore: any, options: any = {}) {
    requiredValidate.call(this, options, ['billNo', 'mode']);
    const { pageStore = {} } = rootStore;
    const { query = {} } = pageStore;
    const { billNo, title, code, status, serviceCode, filterPermission = true, ...params } = options;
    const mode = (status ?? params.mode);

    const titleModeMap = {
      [PageStatusType.ADD]: lang.templateByUuid('UID:P_CORE-YNF-FE_191DEBDE0590001D', '新增', undefined, { returnStr: true }),
      [PageStatusType.EDIT]: lang.templateByUuid('UID:P_CORE-YNF-FE_191DEBDE0590001E', '编辑', undefined, { returnStr: true }),
      [PageStatusType.BROWSE]: params?.code?.slice(-4) ?? lang.templateByUuid('UID:P_CORE-YNF-FE_191DEBDE0590001F', '浏览', undefined, { returnStr: true }),
    }

    window.jDiwork.openService(serviceCode || query?.serviceCode, { ...query, tplId: '', billNo }, {
      title: title ?? `${titleModeMap[mode]}${document.title ?? ''}`,
      code: code ?? `${billNo}_${mode === PageStatusType.ADD ? PageStatusType.ADD: ''}`,
      serviceType: 'ynf',
      data: {
        pane: { query: { ...query, tplId: '' }, billNo, params, status: mode },
        isMergeFlag: true
      },
      filterPermission: filterPermission
    });
  }
}

/**
 * 打开子页面
 * @param rootStore
 * @param options {
 *  // 选填字段
 *  pageCode: string
 * }
 * @returns
 * @hooks {
 *  // 专有钩子
 *  // 公共钩子 
 *  beforeHook: 开始钩子 
 *  afterHook: 结束钩子
 *  catchHook: 异常钩子
 *  finallyHook: 最终钩子
 * }
 */
export async function openSubPage(rootStore: any, options: any = {}, event: any, eventOptions: any) {
  const { pageStore = {} } = rootStore;
  try {
    return pageStore.showPage(options?.pageCode ?? eventOptions?.pageCode);
  } catch (err: any) {
    return Promise.reject(err);
  }
}

/**
 * 关闭子页面
 * @param rootStore
 * @param options {
 *  // 选填字段
 *  pageCode: string
 * }
 * @returns
 * @hooks {
 *  // 专有钩子
 *  // 公共钩子 
 *  beforeHook: 开始钩子 
 *  afterHook: 结束钩子
 *  catchHook: 异常钩子
 *  finallyHook: 最终钩子
 * }
 */
export async function closeSubPage(rootStore: any, options: any = {}, event: any, eventOptions: any) {
  const { pageStore = {} } = rootStore;
  try {
    return pageStore.closePage(options?.pageCode ?? eventOptions?.pageCode);
  } catch (err: any) {
    return Promise.reject(err);
  }
}

/**
 * openSubPageByRow
 * 移动端打开子表-行操作
 * @param rootStore
 * @param options {
 * }
 */
export async function openSubPageByRow(rootStore: any, options: any = {}, rowIndex: any) {
  requiredValidate.call(this, options, ['cardStoreKey']);
  const { cardStoreKey = 'cardStore' } = options;
  const cardStore = rootStore.get(cardStoreKey);
  try {
    const [hoverRow, tableStore] = getHoverRow.apply(this, arguments);
    return cardStore.openSubPageByRow({...options, rowIndex, hoverRow});
  } catch (err: any) {
    return Promise.reject(err);
  }
}

/**
 * saveSubRow
 * 移动端保存子表
 * @param rootStore
 * @param options {
 * }
 */
export async function saveSubRow(rootStore: any, options: any = {}) {
  requiredValidate.call(this, options, ['cardStoreKey', 'subFormStoreKey']);
  const { cardStoreKey = 'cardStore', subFormStoreKey } = options;
  const cardStore = rootStore.get(cardStoreKey);
  const subFormStore = rootStore.get(subFormStoreKey);
  if (!cardStore) throw (new Error(`The CardStore[${cardStoreKey}] not found`));
  // cardStore 是必填项首先检查, 然后校验表单
  const formValidateRes = subFormStore.validate()
  if (formValidateRes.hasErr) {
    rootStore.utils.message.danger({
      content: <ErrDisplay terminalType={rootStore.pageStore?.params?.terminalType} errors={{ form: formValidateRes }}/>
    })
    this.abort({ log: lang.templateByUuid('UID:P_CORE-YNF-FE_18951C6405B8002D', '表单校验失败，请检查') });
  }
  try {
    return cardStore.saveSubRow({...options});
  } catch (err: any) {
    return Promise.reject(err);
  }
}

export async function openModalForAdd(rootStore: any, options: any = {}) {
  const { pageStore = {} } = rootStore;
  const { params = {} } = pageStore;
  const { formStoreKey } = options;
  const formStore = rootStore.get(formStoreKey);
  // 准备add接口的数据
  const data = {}
  if (formStore?.masterOrgField) {
    Object.assign(data, { data: {
      ...formStore.masterOrgInfo,
      domain: formStore.masterOrgInfo.domain || params.domainKey
    }})
  }
  // 请求add接口
  const addRes = await services.add({
    params: {
      billnum: options.billNo,
    },
    data
  });
  // add接口返回的数据剔除主组织，主组织需要通过masterOrgInfo带入
  if (addRes.data?.masterOrgKeyField) {
    delete addRes.data[addRes.data.masterOrgKeyField]
  }
  formStore.clear()
  formStore.setUiState(PageStatusType.ADD)
  formStore.setEntityValue(addRes.data)
  // add接口返回的数据有masterOrgInfo的话就触发参照带入，把主组织的相关字段带入
  if (addRes.data?.masterOrgInfo && formStore.masterOrgField) {
    const [firstItem] = Object.values(addRes.data.masterOrgInfo)
    if (firstItem.length) {
      const orgData = firstItem[0]
      // masterOrgInfo多语环境下，返回的name是对象，要根据环境取值
      if (typeof orgData.name === 'object') {
        let locale = pageStore.context?.locale ?? 'zh-CN'
        locale = locale.replace('-', '_')
        orgData.name = orgData.name[locale]
      }
      await formStore.get(formStore.masterOrgField).setValue(orgData, false)
    }
  }
  // 显示弹窗
  formStore.setValue('modalVisible', true)
}

export async function openEditModalByRow(rootStore: any, options: any = {}) {
  options.mode = PageStatusType.EDIT;
  return openBrowseModalByRow.apply(this, arguments);
}

export async function openBrowseModalByRow(rootStore: any, options: any = {}) {
  const [hoverRow, tableStore] = getHoverRow.apply(this, arguments);
  if (!hoverRow) throw (new Error(`The hoverRow not found`));

  const { formStoreKey } = options;
  const formStore = rootStore.get(formStoreKey);

  const detailRes = await services.detail({
    params: {
      id: hoverRow.getValue('id') ?? '',
      billnum: options.billNo
    },
    data: {
      alias: options.dataSourceAlias || 'mainEntity',
      main: true
    }
  });
  formStore.clear()
  formStore.setUiState(options.mode || PageStatusType.BROWSE)
  formStore.setEntityValue(detailRes.data)
  formStore.setValue('modalVisible', true)
}

export async function openCopyModalByRow(rootStore: any, options: any = {}) {
  const [hoverRow, tableStore] = getHoverRow.apply(this, arguments);
  if (!hoverRow) throw (new Error(`The hoverRow not found`));

  const { formStoreKey } = options;
  const formStore = rootStore.get(formStoreKey);

  const detailRes = await services.detail({
    params: {
      id: hoverRow.getValue('id') ?? '',
      billnum: options.billNo
    },
    data: {
      alias: options.dataSourceAlias || 'mainEntity',
      main: true
    }
  });
  const copyRes = await services.copy({ params: {
    id: hoverRow.getValue('id') ?? '',
    billnum: options.billNo,
    initiator: 'COPY',
    datasource: options.dataSourceAlias || 'mainEntity'
  }
  });

  const [dataSource, dataSourceLevel] = getDataSourceByAlias(options.dataSourceAlias, rootStore.dataSource);
  const mergeRes = mergeDetailCopyData(dataSource, detailRes.data, copyRes.data);

  formStore.clear()
  formStore.setUiState(PageStatusType.ADD)
  formStore.setEntityValue(mergeRes)
  formStore.setValue('modalVisible', true)
}

export async function saveFormModal(rootStore: any, options: any = {}) {
  const { formStoreKey } = options;
  const formStore = rootStore.get(formStoreKey);
  let data = {
    ...formStore.getValue()
  }
  await services.save({
    params: {
      mode: formStore.uiState,
      billnum: options.billNo,
      billId: formStore.getValue('id')
    },
    data: { data }
  })
  formStore.setValue('modalVisible', false)
  rootStore.pageStore.refresh()
}

export async function saveFormModalAndAdd(rootStore: any, options: any = {}) {
  const { formStoreKey } = options;
  const formStore = rootStore.get(formStoreKey);
  let data = {
    ...formStore.getValue()
  }
  await services.save({
    params: {
      mode: formStore.uiState,
      billnum: options.billNo,
      billId: formStore.getValue('id')
    },
    data: { data }
  })
  openModalForAdd.apply(this, arguments);
}

export async function closeFormModal(rootStore: any, options: any = {}) {
  const { formStoreKey } = options;
  const formStore = rootStore.get(formStoreKey);
  formStore.setValue('modalVisible', false)
}
